/**
 * autismFirestoreQueries.js
 *
 * Helpers for building Firestore collection queries against Adult Autism
 * subcollections in a way that satisfies the get()-free security rules.
 *
 * Firestore list queries (getDocs) are NOT filters: if the rules check fields on
 * resource.data, the client query must include matching where() constraints or
 * the entire request fails with permission-denied — even when every document
 * would pass individually.
 *
 * Org Admin / Support use a path-based rule (isSameOrgAsPatient) so they can
 * issue unconstrained collection reads. Assigned clinicians must filter on the
 * denormalised access fields that the rules check:
 *   - organisation (must match the caller's org claim)
 *   - assignedClinicianIds (array-contains the caller's UID)
 */

import { collection, query, where } from 'firebase/firestore';

/** Provider roles that read autism subcollections via the patient header path. */
export function hasOrgWideAutismSubcollectionAccess(userType) {
  return userType === 'Org. Admin' || userType === 'Support';
}

/**
 * Build a getDocs-ready query for an autism subcollection (formInstances or
 * assessmentStages) hanging off a patient or informant assignment tree.
 *
 * @param {import('firebase/firestore').Firestore} firestoreDb
 * @param {string[]} pathSegments - e.g. ['patients', uid, 'adult_autism', 'assessment_001', 'formInstances']
 * @param {object} access
 * @param {string} access.organisation - Org name from the patient header or claims
 * @param {string} [access.clinicianUid] - Required when isOrgWideAccess is false
 * @param {boolean} access.isOrgWideAccess - True for Org Admin / Support
 */
export function buildAutismSubcollectionQuery(firestoreDb, pathSegments, access) {
  const coll = collection(firestoreDb, ...pathSegments);

  if (access?.isOrgWideAccess) {
    return coll;
  }

  const organisation = access?.organisation || '';
  const clinicianUid = access?.clinicianUid || '';

  return query(
    coll,
    where('organisation', '==', organisation),
    where('assignedClinicianIds', 'array-contains', clinicianUid),
  );
}
